import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Copy, Plus, Minus } from 'lucide-react'
import { useWalletStore } from '@/stores/useWalletStore'
import { getLiquidityLog } from '@/services/api/swap'
import { getCoinIcon } from '@/lib/common'
import { useClipboard } from '@/hooks/useClipboard'
import { NoData } from '@/components/NoData'
import type { LiquidityLog } from '@/types'

export function LiquidityRecord() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLogin, session } = useWalletStore()
  const { copy } = useClipboard()

  const [list, setList] = useState<LiquidityLog[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const pageSize = 20

  const fetchLogs = useCallback(async (pageNum: number, append = false) => {
    if (!isLogin || !session) return
    setLoading(true)
    try {
      const result = await getLiquidityLog({
        account: session.account.name,
        page: pageNum,
        size: pageSize,
      })
      if (result.status && result.data) {
        const data = result.data as LiquidityLog[]
        if (append) {
          setList((prev) => [...prev, ...data])
        } else {
          setList(data)
        }
        setHasMore(data.length >= pageSize)
      } else {
        if (!append) setList([])
        setHasMore(false)
      }
    } catch {
      if (!append) setList([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [isLogin, session])

  useEffect(() => {
    fetchLogs(1)
  }, [fetchLogs])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchLogs(nextPage, true)
  }

  const formatTime = (ts: string) => {
    const date = new Date(ts)
    return date.toLocaleString()
  }

  return (
    <div className="w-full max-w-[600px] mx-auto px-4 py-6 md:py-10 animate-trade-enter">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate({ to: '/pool' })} className="p-1.5 rounded-full hover:bg-[#edf5ed] transition-colors">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <h2 className="text-[20px] font-bold text-foreground">{t('swap.liquidityRecord')}</h2>
      </div>

      {!isLogin ? (
        <div className="text-center py-10 text-nav">{t('public.noauth')}</div>
      ) : list.length === 0 && !loading ? (
        <NoData />
      ) : (
        <div className="space-y-3">
          {list.map((item, i) => {
            const isAdd = item.direction === 1
            return (
              <div key={item.txid || i} className="bg-white rounded-2xl border border-[#e5e5e5] px-4 py-3">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <img
                        src={getCoinIcon('', item.symbol0 || '')}
                        className="w-[24px] h-[24px] rounded-full bg-gray-100"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <span className="text-[14px] font-semibold text-foreground">{item.symbol0}</span>
                    </div>
                    {isAdd ? (
                      <Plus className="h-4 w-4 text-primary" />
                    ) : (
                      <Minus className="h-4 w-4 text-red-400" />
                    )}
                    <div className="flex items-center gap-1">
                      <img
                        src={getCoinIcon('', item.symbol1 || '')}
                        className="w-[24px] h-[24px] rounded-full bg-gray-100"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <span className="text-[14px] font-semibold text-foreground">{item.symbol1}</span>
                    </div>
                  </div>
                  <span className={`text-[12px] px-2 py-0.5 rounded-full ${
                    isAdd ? 'bg-[#edf5ed] text-primary' : 'bg-red-50 text-red-500'
                  }`}>
                    {isAdd ? t('swap.add') : t('swap.remove')}
                  </span>
                </div>

                {/* Amounts */}
                <div className="space-y-1 text-[13px] mb-2">
                  <div className="flex justify-between">
                    <span className="text-nav">{item.symbol0}</span>
                    <span className="text-foreground">{item.amount0?.replace('-', '')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-nav">{item.symbol1}</span>
                    <span className="text-foreground">{item.amount1?.replace('-', '')}</span>
                  </div>
                  {item.liquidity && (
                    <div className="flex justify-between">
                      <span className="text-nav">{t('swap.certificate')}</span>
                      <span className="text-foreground">{item.liquidity}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-[12px] border-t border-[#f3f3f3] pt-2">
                  <span className="text-nav">{formatTime(item.timestamp)}</span>
                  {item.txid && (
                    <div className="flex items-center gap-1">
                      <a
                        href={`https://bloks.io/transaction/${item.txid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {item.txid.slice(0, 12)}...
                      </a>
                      <button onClick={() => copy(item.txid)} className="p-0.5 hover:text-primary transition-colors">
                        <Copy className="h-3.5 w-3.5 text-nav" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="w-full py-3 text-[14px] text-nav hover:text-primary transition-colors"
            >
              {loading ? t('public.loading') : t('swap.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
